
import moment from "moment";
import { Component, Vue, Watch } from "vue-property-decorator";
import CancelButton from "../buttons/CancelButton.vue";
import DangerButton from "../buttons/DangerButton.vue";
import ContainerFrame from "../ContainerFrame.vue";
import CreateBookingDialog from "./CreateBookingDialog.vue";
import { StationsModule } from "@/store/modules/StationsModule";
import { StationSummary } from "@/api/dtos/StationSummary.schema";
import { Card } from "@/api/dtos/Station.schema";
import { RentalMethod } from "@/api/dtos/RentalMethod.schema";
import { getBookingCardOccupations } from "@/api/stations";
import {
  AvailableDevices,
  Device,
  DeviceWithStation,
  SmallStationDTO,
  DeviceWithStationFlat,
} from "@/api/dtos/AvailableDevices.schema.d";
import api from "@/api";

import { showToast } from "@/utils/toastService";
import { getAxiosResponse } from "@/utils/errorUtils";
import {
  formatBookingWallTimeForApi,
  getBookingTimeZone,
} from "@/utils/bookingDateFormatter";

interface Box {
  id: number;
  station: string;
  type: string;
  number: string;
  features: string[];
  price: number;
}

@Component({
  components: {
    CancelButton,
    DangerButton,
    ContainerFrame,
    CreateBookingDialog,
  },
})
export default class CreateBooking extends Vue {
  dateRange: [string, string] = [
    moment().toISOString(),
    moment().add(1, "day").toISOString(),
  ];

  selectedRentalMethod: RentalMethod | null = null;
  selectedStations: StationSummary[] | null = null;
  selectedDevice: number | null = null;
  emailToBookFor: string | null = null;
  selectedDeviceTypes: string[] | null = null;
  selectedDeviceProperties: string[] | null = null;
  selectedCard: Card | null = null;
  availableDevices: AvailableDevices | null = null;
  formIsValid = false;
  startDatemenu = false;
  endDatemenu = false;
  startTimeMenu = false;
  endTimeMenu = false;
  price: string | null = null;
  startDate: string = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .substr(0, 10);
  // startTime = null;
  endDate: string = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .substr(0, 10);
  // endTime = null;
  startTime = "00:00";
endTime = "23:59";
  emailValidationErrors: string[] = [];
  showBookingDialog = false;
  selectedBox: DeviceWithStationFlat | null = null;

  cardOccupations: any[] = [];
  loadingCardOccupations = false;
  cardOccError: string | null = null;
  lastAvailabilityRange: {
    start: string;
    end: string;
    timeZone: string;
  } | null = null;

  mounted(): void {
    StationsModule.fetchStationSummaries();
  }

  formatDateDisplay(date: string | null): string {
    if (!date) return "";
    return new Date(date).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }

  private ensureValidDateRange(): boolean {
    if (!this.startDateTime || !this.endDateTime) return false;

    const start = this.localBookingDateTimeToComparableDate(this.startDateTime);
    const end = this.localBookingDateTimeToComparableDate(this.endDateTime);

    if (end <= start) {
      const correctedEnd = moment(this.startDateTime).add(1, "day");

      this.endDate = correctedEnd.format("YYYY-MM-DD");
      this.endTime = correctedEnd.format("HH:mm");

      this.availableDevices = null;
      return true;
    }

    return false;
  }

  async tryFetchAvailableDevices(): Promise<void> {
    if (!this.endDateTime || !this.startDateTime) {
      this.availableDevices = null;
      return;
    }

     if (this.ensureValidDateRange()) {
        return;
      }
    const range = this.createBookingRequestRange(this.selectedBookingTimeZone);
    this.lastAvailabilityRange = range;

    this.availableDevices = await api.StationsApi.filterAvailableDevices(
      this.selectedStations ? this.selectedStations.map((s) => s.uuid) : [],
      range.start,
      range.end,
      this.selectedRentalMethod ?? undefined,
      this.selectedDeviceTypes ? this.selectedDeviceTypes : undefined,
      this.selectedDeviceProperties ?? undefined
    );
    if (!this.emailToBookFor) {
      this.emailToBookFor = this.availableDevices.defaultMail;
    }
    this.sanitizeSelection();
    (this.$refs.form as any).validate();
  }

  onStartTimeBlur(): void {
    if (!this.startTime || !this.startTime.trim()) {
      this.startTime = "00:00";
    }
  }

  onEndTimeBlur(): void {
    if (!this.endTime || !this.endTime.trim()) {
      this.endTime = "23:59";
    }
  }

  private syncEndToStartIfNeeded(): boolean {
    if (!this.startDateTime || !this.endDateTime) return false;

    const start = this.localBookingDateTimeToComparableDate(this.startDateTime);
    const end = this.localBookingDateTimeToComparableDate(this.endDateTime);

    if (start > end) {
      this.endDate = this.startDate;
      this.endTime = this.startTime;
      return true;
    }

    return false;
  }

  @Watch("startDateTime")
  @Watch("endDateTime")
  onDateTimeChanged(): void {
    if (this.syncEndToStartIfNeeded()) {
      return;
    }

    this.tryFetchAvailableDevices();
  }

  @Watch("selectedRentalMethod")
  @Watch("selectedStations")
  @Watch("selectedDevice")
  @Watch("selectedCard")
  @Watch("emailToBookFor")
  @Watch("selectedDeviceTypes")
  @Watch("selectedDeviceProperties")
  // @Watch("startDateTime")
  // @Watch("endDateTime")
  onQueryChanged(): void {
    this.tryFetchAvailableDevices();
  }

  @Watch("selectedDeviceProperties", { deep: true })
  onPropsChanged(): void {
    // clean up selectedDeviceTypes (now array) if some types are no longer available
    if (this.selectedDeviceTypes && this.selectedDeviceTypes.length) {
      // const validTypes = this.selectedDeviceTypes.filter((t) =>
      //   this.availableDeviceTypesForSelection.includes(t)
      // );
      // this.selectedDeviceTypes = validTypes.length ? validTypes : null;
    }

    if (
      this.selectedDevice &&
      !this.availableDeviceNumbers.includes(this.selectedDevice)
    ) {
      this.selectedDevice = null;
    }
  }

  @Watch("selectedDeviceTypes")
  onTypeChanged(): void {
    if (
      this.selectedDevice &&
      !this.availableDeviceNumbers.includes(this.selectedDevice)
    ) {
      this.selectedDevice = null;
    }
  }

  get stations(): StationSummary[] {
    return StationsModule.stationSummaries;
  }

  get allAvailableDevices(): DeviceWithStationFlat[] {
    if (!this.availableDevices) return [];

    const devicesWithStation = this.availableDevices.devices as DeviceWithStation[];

    return devicesWithStation.map((dws) => ({
      ...dws.device,
      deviceNumber: dws.device.deviceNumber,
      stationId: dws.station.id,
      stationName: dws.station.name,
      bookingTimeZone: dws.station.bookingTimeZone,
      defaultBookingEmail: dws.station.defaultBookingEmail,
      cards: dws.station.cards
    }));
  }

  // get allAvailableDevices() {
  //   if (!this.availableDevices) return [];

  //   const defaultMail = this.availableDevices.defaultMail;

  //   return this.availableDevices.devices.map(device => ({
  //     ...device,
  //     defaultMail
  //   }));
  // }

  get cardsToDisplay(): Card[] {
    return this.availableDevices ? this.availableDevices.cards : [];
  }

  getAssignedType(device: DeviceWithStationFlat): string {
    return device.typeSettings?.assignedType ?? "-";
  }

  getAssignedProperties(device: DeviceWithStationFlat): string[] {
    return device.typeSettings?.assignedProperties ?? [];
  }

  get allAvailableRentalMethods() {
    return this.allAvailableDevices.map((x) => x.rentalMethod);
  }

  get startDateDisplay() {
    return this.formatDateDisplay(this.startDate);
  }

  get endDateDisplay() {
    return this.formatDateDisplay(this.endDate);
  }

  get startDateTime(): string | null {
    if (!this.startDate || !this.startTime) {
      return null;
    }

    return `${this.startDate}T${this.startTime}:00`;
  }

  get endDateTime(): string | null {
    if (!this.endDate || !this.endTime) {
      return null;
    }

    return `${this.endDate}T${this.endTime}:00`;
  }

  get allAvailablePropertiesForSelection(): string[] {
    const props = new Set<string>();
    this.allAvailableDevices.forEach((d) => {
      this.getAssignedProperties(d).forEach((p: string) => props.add(p));
    });
    return Array.from(props);
  }

  get filteredDevicesByProps() {
    if (!this.selectedDeviceProperties || this.selectedDeviceProperties.length === 0)
      return this.allAvailableDevices;
    return this.allAvailableDevices.filter((d) =>
      (this.selectedDeviceProperties as string[]).every((p) =>
        this.getAssignedProperties(d).includes(p)
      )
    );
  }

  get filteredDevicesByType() {
    const base = this.filteredDevicesByProps;

    if (!this.selectedDeviceTypes || this.selectedDeviceTypes.length === 0) {
      return base;
    }

    return base.filter((d) =>
      this.selectedDeviceTypes!.includes(this.getAssignedType(d))
    );
  }

  get availableDevicePropertiesForSelection(): string[] {
    return this.allAvailablePropertiesForSelection;
  }

  get availableDeviceTypesForSelection(): string[] {
    const types = new Set<string>();
    this.filteredDevicesByProps.forEach((d) => {
      const assignedType = this.getAssignedType(d);
      if (assignedType !== "-") {
        types.add(assignedType);
      }
    });
    return Array.from(types);
  }

  get stationSelectItems(): {
    value: StationSummary;
    text: string;
  }[] {
    return this.stations.map((s) => {
      return {
        value: s,
        text: s.stationLocation?.stationName,
      };
    });
  }

  get selectedBookingTimeZone(): string | null {
    const selectedStations = this.selectedStations ?? [];

    if (!selectedStations.length) {
      return null;
    }

    const selectedStationTimeZones = selectedStations.map(
      (station) => station.bookingTimeZone
    );

    if (selectedStationTimeZones.some((timeZone) => !timeZone)) {
      return null;
    }

    const uniqueTimeZones = Array.from(
      new Set(selectedStationTimeZones as string[])
    );
    return uniqueTimeZones.length === 1 ? uniqueTimeZones[0] : null;
  }

  get currentDialogBookingTimeZone(): string | null {
    return this.selectedBox?.bookingTimeZone ?? null;
  }

  get bookingTimeBasisText(): string {
    const selectedStations = this.selectedStations ?? [];

    if (selectedStations.length === 1) {
      const station = selectedStations[0];
      const stationName =
        station.stationLocation?.stationName ?? "Ausgewählte Station";

      if (station.bookingTimeZone) {
        return `Zeiten gelten für: ${stationName}, Gerätezeit ${station.bookingTimeZone}`;
      }
    }

    if (selectedStations.length > 1 && this.selectedBookingTimeZone) {
      return `Zeiten gelten für alle ausgewählten Stationen in Gerätezeit ${this.selectedBookingTimeZone}`;
    }

    return "Die Zeiten werden nach Auswahl der Box in der Gerätezeitzone geprüft.";
  }

  get sortedDateRange(): [string, string] {
    return this.dateRange
      .map((s) => new Date(s))
      .sort((d1, d2) => d1.getTime() - d2.getTime())
      .map((d) => d.toISOString()) as any;
  }

  get availableDeviceNumbers(): number[] {
    return this.filteredDevicesByType.map(
      (device: any) => device.deviceNumber ?? device.number
    );
  }

  get chipCardSelectItems(): {
    value: Card;
    text: string;
  }[] {
    if (!this.availableDevices) return [];
    return this.availableDevices.cards.map((card) => {
      return {
        value: card,
        text: card.name,
      };
    });
  }

  async validateEmail(): Promise<void> {
    if (!this.emailToBookFor) return;
    const isValidEmailErrorMessage = this.$validation.vuetify.single({
      presence: true,
      email: true,
    })(this.emailToBookFor);
    if (typeof isValidEmailErrorMessage === "string") {
      this.emailValidationErrors = [isValidEmailErrorMessage];
      return;
    }
    // try {
    //   const userExists = await api.UsersApi.userExists(this.emailToBookFor);
    //   this.emailValidationErrors = userExists ? [] : ["Nutzer existiert nicht"];
    // } catch (e) {
    //   this.emailValidationErrors = ["Nutzer existiert nicht"];
    // }
  }

  async onCreateBooking(payload: any): Promise<void> {
    if (
      !this.formIsValid ||
      !this.startDateTime ||
      !this.endDateTime ||
      !payload.device ||
      !payload.email
    )
    return;
    try {
      const booking = await api.BookingsApi.createBooking({
        device: payload.device.number ?? payload.device.deviceNumber,
        cardUid: payload.card?.value?.uid ?? null,
        start: this.currentBookingRequestRange(payload.device).start,
        end: this.currentBookingRequestRange(payload.device).end,
        stationId: payload.device.stationId,
        mail: payload.email,
      });

      (this as any).$toast.success(
        `Buchung ${booking.bookingId} wurde erstellt – anklicken zum Öffnen`,
        {
          toastClassName: "clickable-toast",
          onClick: (closeToast: () => void) => {
            closeToast();
            this.$router
              .push({ path: "/booking", query: { booking: booking.bookingId } })
              .catch(() => undefined);
          },
        }
      );
      this.price = ""
      this.selectedBox = null;
      this.showBookingDialog = false;
      this.tryFetchAvailableDevices();
      // location.reload();
    } catch (e) {
      showToast({
        response: getAxiosResponse(e),
        message: "Buchung konnte nicht erstellt werden",
        type: "error",
      });
    }
  }

  private sanitizeSelection(): void {
    if (this.selectedDeviceTypes && this.selectedDeviceTypes.length) {
      // const validTypes = this.selectedDeviceTypes.filter((t) =>
      //   this.availableDeviceTypesForSelection.includes(t)
      // );
      // if (validTypes.length !== this.selectedDeviceTypes.length) {
      //   this.selectedDeviceTypes = validTypes.length ? validTypes : null;
      // }
    }

    if (
      this.selectedDevice &&
      !this.availableDeviceNumbers.includes(this.selectedDevice)
    ) {
      this.selectedDevice = null;
    }
  }

  onSearch(): void {
    this.tryFetchAvailableDevices();
  }

  async onBook(box: DeviceWithStationFlat): Promise<void> {
    this.cardOccError = null;
    this.loadingCardOccupations = true;

    try {
      this.price = await this.getBoxPrice(box);

      if (!this.startDateTime || !this.endDateTime) return;

      const range = this.currentBookingRequestRange(box);

      const occ = await getBookingCardOccupations(
        box.stationId,
        range.start,
        range.end
      );

      this.cardOccupations = occ.cards ?? [];
      this.selectedBox = box;
      this.showBookingDialog = true;
    } catch (e) {
      this.cardOccError = "Kartenbelegung konnte nicht geladen werden";
      showToast({ message: this.cardOccError, type: "error" });
    } finally {
      this.loadingCardOccupations = false;
    }
  }

  async checkAvailablityForBox(): Promise<void> {
    if (!this.selectedBox || !this.startDateTime || !this.endDateTime) return;

    const range = this.currentBookingRequestRange(this.selectedBox);

    const occ = await getBookingCardOccupations(
      this.selectedBox.stationId,
      range.start,
      range.end
    );

    const blockedUids = new Set(
      (occ.cards ?? []).filter((c) => c.count > 0).map((c) => c.uid)
    );

    // console.log("Booking card occupations:", occ);
    // console.log("Blocked cards:", [...blockedUids]);
  }

  async getBoxPrice(box: DeviceWithStationFlat):Promise<string>{
    // console.log("called",box);
     if (!this.endDateTime || !this.startDateTime) {
      return "";
    }

    const range = this.currentBookingRequestRange(box);

    return await api.StationsApi.getBoxPrice(
      box.stationId,
      range.start,
      range.end,
      box
    );
  }

  private localBookingDateTimeToComparableDate(value: string): Date {
    return moment(value).toDate();
  }

  private currentBookingRequestRange(box?: DeviceWithStationFlat | null): {
    start: string;
    end: string;
    timeZone: string;
  } {
    const boxTimeZone = box?.bookingTimeZone
      ? getBookingTimeZone(box.bookingTimeZone)
      : null;

    if (
      this.lastAvailabilityRange &&
      this.lastAvailabilityRangeMatchesCurrentInput() &&
      (!boxTimeZone || this.lastAvailabilityRange.timeZone === boxTimeZone)
    ) {
      return this.lastAvailabilityRange;
    }

    return this.createBookingRequestRange(
      box?.bookingTimeZone ?? this.selectedBookingTimeZone
    );
  }

  private createBookingRequestRange(bookingTimeZone?: string | null): {
    start: string;
    end: string;
    timeZone: string;
  } {
    if (!this.startDateTime || !this.endDateTime) {
      throw new Error("Cannot create booking range without start and end");
    }

    const timeZone = getBookingTimeZone(bookingTimeZone);
    return {
      start: formatBookingWallTimeForApi(this.startDateTime, timeZone),
      end: formatBookingWallTimeForApi(this.endDateTime, timeZone),
      timeZone,
    };
  }

  private lastAvailabilityRangeMatchesCurrentInput(): boolean {
    if (!this.lastAvailabilityRange || !this.startDateTime || !this.endDateTime) {
      return false;
    }

    return (
      this.lastAvailabilityRange.start ===
        formatBookingWallTimeForApi(
          this.startDateTime,
          this.lastAvailabilityRange.timeZone
        ) &&
      this.lastAvailabilityRange.end ===
        formatBookingWallTimeForApi(
          this.endDateTime,
          this.lastAvailabilityRange.timeZone
        )
    );
  }
}
